import React from 'react';
import { NodeProps, useReactFlow } from 'reactflow';
import CopyToClipboard from 'react-copy-to-clipboard';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faClipboard, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { IoCheckmarkCircleOutline, IoWarningOutline } from 'react-icons/io5';
import { AuthenticateSelect } from '../../../../auth/select';
import { Select } from '../../../../form/select';
import { SettingsBlock, settingsDataType } from '../../../../settingsBlock';
import { ExpandedBlock } from '../../../expanded';
import { updateNodeValue } from '../../../../../services/method';
import { operationType } from '../../../../../services/types';
import { selectDataType } from '../../../../../services/types';
import { webURL } from '../../../../../services/API';
import { Loader, Main, Styled } from './style';

type TeamsTriggerProps = NodeProps<operationType> & {
	setChildMinimize?: (fn: (id: string) => void) => void;
	setChildSettings?: (fn: (id: string) => void) => void;
};

const scopeOptions: selectDataType[] = [
	{ id: 'channel', name: 'Channel', refId: 'channel', isActive: true },
	{ id: 'chat', name: 'Chat', refId: 'chat' },
];

const eventModeOptions: selectDataType[] = [
	{ id: 'message', name: 'Message', refId: 'message', isActive: true },
	{ id: 'file', name: 'File', refId: 'file' },
	{ id: 'message_or_file', name: 'Message or file', refId: 'message_or_file' },
];

function toSelectItems(items: any[] = [], activeId?: string) {
	return items.map((item, index) => ({
		id: item.id,
		refId: item.id,
		name: item.name || item.displayName || item.topic || item.id || `Item ${index + 1}`,
		isActive: activeId ? item.id === activeId : index === 0,
	}));
}

function getActiveId(items?: any[]) {
	return Array.isArray(items) ? (items.find(item => item.isActive)?.id || items[0]?.id || '') : '';
}

function cacheBust() {
	return `_=${Date.now()}`;
}

export function TeamsTrigger({ id, data, setChildMinimize, setChildSettings }: TeamsTriggerProps) {
	const { setNodes } = useReactFlow();
	const [settings, setSettings] = React.useState<settingsDataType>({ isToggle: false });
	const [isExpanded, setIsExpanded] = React.useState(false);
	const [isCopy, setIsCopy] = React.useState(false);
	const [showNotificationUrl, setShowNotificationUrl] = React.useState(false);
	const [link, setLink] = React.useState('');
	const [isLoading, setIsLoading] = React.useState(false);
	const [isActionLoading, setIsActionLoading] = React.useState(false);
	const [message, setMessage] = React.useState('');
	const [searchParams] = useSearchParams();
	const projectId = searchParams.get('id');
	const connectionId = data.value?.connectionId || data.value?.connection?.id || data.value?.connection?._id || '';
	const scopeType = data.value?.scopeType || 'channel';
	const eventMode = data.value?.eventMode || 'message';

	React.useEffect(() => {
		if (setChildMinimize) setChildMinimize(() => { setIsExpanded(prev => !prev) });
		if (setChildSettings) setChildSettings(() => { setSettings(prev => ({ ...prev, isToggle: !prev.isToggle })) });
	}, [setChildMinimize, setChildSettings]);

	React.useEffect(() => {
		if (!isExpanded) setShowNotificationUrl(false);
	}, [isExpanded]);

	React.useEffect(() => {
		if (!data.value) {
			updateNodeValue(id, setNodes, {
				connection: null,
				connectionId: null,
				scopeType: 'channel',
				eventMode: 'message',
				teams: [],
				channels: [],
				chats: [],
				status: 'disabled',
				fileStatus: 'disabled',
			});
		}
	}, [data.value, id, setNodes]);

	const handleAuthenticate = async (type: 'add' | 'remove', connection?: any) => {
		if (type === 'add') {
			updateNodeValue(id, setNodes, {
				connection,
				connectionId: connection?.id || connection?._id || null,
				teams: [],
				channels: [],
				chats: [],
				status: 'disabled',
				subscriptionId: '',
				expirationDateTime: '',
				fileSubscriptionId: '',
				fileExpirationDateTime: '',
				fileStatus: 'disabled',
			});
		} else {
			updateNodeValue(id, setNodes, {
				connection: null,
				connectionId: null,
				teams: [],
				channels: [],
				chats: [],
				status: 'disabled',
				subscriptionId: '',
				expirationDateTime: '',
				fileSubscriptionId: '',
				fileExpirationDateTime: '',
				fileStatus: 'disabled',
			});
		}
	};

	const handleSelectScope = (selectId: any) => {
		updateNodeValue(id, setNodes, {
			scopeType: selectId,
			status: 'disabled',
			subscriptionId: '',
			expirationDateTime: '',
			fileSubscriptionId: '',
			fileExpirationDateTime: '',
			fileStatus: 'disabled',
		});
	};

	const handleSelectEventMode = (selectId: any) => {
		updateNodeValue(id, setNodes, {
			eventMode: selectId,
			status: 'disabled',
			subscriptionId: '',
			expirationDateTime: '',
			fileSubscriptionId: '',
			fileExpirationDateTime: '',
			fileStatus: 'disabled',
		});
	};

	const handleSelectCollection = (key: 'teams' | 'channels' | 'chats', selectId: any) => {
		const next = (data.value?.[key] || []).map((item: any) => ({ ...item, isActive: item.id === selectId }));
		const selected = next.find((item: any) => item.isActive);
		const update: any = {
			[key]: next,
			status: 'disabled',
			subscriptionId: '',
			expirationDateTime: '',
			fileSubscriptionId: '',
			fileExpirationDateTime: '',
			fileStatus: 'disabled',
		};
		if (key === 'teams') update.teamId = selected?.id || '';
		if (key === 'channels') update.channelId = selected?.id || '';
		if (key === 'chats') update.chatId = selected?.id || '';
		updateNodeValue(id, setNodes, update);
	};

	const fetchTeams = async () => {
		if (!connectionId) return;
		setIsActionLoading(true);
		setMessage('');
		try {
			const response = await axios.get(`${webURL}/teams/teams?connectionId=${connectionId}&${cacheBust()}`);
			const teams = toSelectItems(response.data?.items || [], data.value?.teamId);
			updateNodeValue(id, setNodes, { teams, teamId: getActiveId(teams), channels: [] });
		} catch (err: any) {
			setMessage(err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Failed to load Teams');
		} finally {
			setIsActionLoading(false);
		}
	};

	const fetchChannels = async () => {
		if (!connectionId) return;
		const teamId = data.value?.teamId || getActiveId(data.value?.teams);
		if (!teamId) return;
		setIsActionLoading(true);
		setMessage('');
		try {
			const response = await axios.get(`${webURL}/teams/channels?connectionId=${connectionId}&teamId=${encodeURIComponent(teamId)}&${cacheBust()}`);
			const channels = toSelectItems(response.data?.items || [], data.value?.channelId);
			updateNodeValue(id, setNodes, { channels, channelId: getActiveId(channels) });
		} catch (err: any) {
			setMessage(err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Failed to load channels');
		} finally {
			setIsActionLoading(false);
		}
	};

	const fetchChats = async () => {
		if (!connectionId) return;
		setIsActionLoading(true);
		setMessage('');
		try {
			const response = await axios.get(`${webURL}/teams/chats?connectionId=${connectionId}&${cacheBust()}`);
			const chats = toSelectItems((response.data?.items || []).map((chat: any) => ({
				id: chat.id,
				name: chat.topic || `${chat.chatType || 'Chat'} ${chat.id?.slice?.(0, 8) || ''}`,
			})), data.value?.chatId);
			updateNodeValue(id, setNodes, { chats, chatId: getActiveId(chats) });
		} catch (err: any) {
			setMessage(err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Failed to load chats');
		} finally {
			setIsActionLoading(false);
		}
	};

	const generateLink = async () => {
		const response = await axios.post(`${webURL}/triggers/teams/generate-link`, {
			nodeIndex: data.index,
			projectId,
			connectionId,
			scopeType,
			eventMode,
			teamId: data.value?.teamId || getActiveId(data.value?.teams),
			channelId: data.value?.channelId || getActiveId(data.value?.channels),
			chatId: data.value?.chatId || getActiveId(data.value?.chats),
		});
		const triggerCredentials = response.data;

		setNodes(prev =>
			prev.map(node =>
				node.data.index === data.index
					? { ...node, data: { ...node.data, triggerCredentials } }
					: node
			)
		);
		setLink(triggerCredentials.webhookUrl);
		return triggerCredentials;
	};

	const retrieveLink = async () => {
		if (data?.triggerCredentials?.webhookUrl) {
			setLink(data.triggerCredentials.webhookUrl);
		} else {
			await generateLink();
		}
	};

	React.useEffect(() => {
		if (!isExpanded || link) return;
		setIsLoading(true);
		retrieveLink().finally(() => setIsLoading(false));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isExpanded, data?.triggerCredentials?.webhookUrl]);

	const enableSubscription = async () => {
		if (!projectId || !connectionId) return;
		setIsActionLoading(true);
		setMessage('');
		try {
			const triggerCredentials = data.triggerCredentials?.webhookUrl ? data.triggerCredentials : await generateLink();
			const response = await axios.post(`${webURL}/triggers/teams/subscription/enable`, {
				projectId,
				nodeId: id,
				nodeIndex: data.index,
				connectionId,
				scopeType,
				eventMode,
				teamId: data.value?.teamId || getActiveId(data.value?.teams),
				channelId: data.value?.channelId || getActiveId(data.value?.channels),
				chatId: data.value?.chatId || getActiveId(data.value?.chats),
			});
			const payload = response.data || {};
			const nextCredentials = payload.triggerCredentials || triggerCredentials;
			setNodes(prev =>
				prev.map(node =>
					node.id === id
						? { ...node, data: { ...node.data, triggerCredentials: nextCredentials } }
						: node
				)
			);
			updateNodeValue(id, setNodes, {
				status: payload.status || 'connected',
				eventMode: payload.eventMode || eventMode,
				subscriptionId: payload.subscriptionId,
				expirationDateTime: payload.expirationDateTime,
				fileSubscriptionId: payload.fileSubscription?.subscriptionId || '',
				fileExpirationDateTime: payload.fileSubscription?.expirationDateTime || '',
				fileStatus: payload.fileSubscription?.status || (((payload.eventMode || eventMode) === 'file' || (payload.eventMode || eventMode) === 'message_or_file') ? 'active' : 'disabled'),
				notificationUrl: payload.notificationUrl,
				resource: payload.resource,
				lastError: '',
				lastRenewedAt: payload.lastRenewedAt || new Date().toISOString(),
			});
			setLink(payload.notificationUrl || nextCredentials.webhookUrl || link);
			// setMessage('Teams trigger enabled.');
		} catch (err: any) {
			const errorMessage = err?.response?.data?.message || err?.message || 'Failed to enable Teams trigger';
			updateNodeValue(id, setNodes, { status: 'error', lastError: errorMessage });
			setMessage(errorMessage);
		} finally {
			setIsActionLoading(false);
		}
	};

	const disableSubscription = async () => {
		if (!projectId) return;
		setIsActionLoading(true);
		setMessage('');
		try {
			await axios.post(`${webURL}/triggers/teams/subscription/disable`, {
				projectId,
				nodeId: id,
				nodeIndex: data.index,
			});
			updateNodeValue(id, setNodes, {
				status: 'disabled',
				subscriptionId: '',
				expirationDateTime: '',
				fileSubscriptionId: '',
				fileExpirationDateTime: '',
				fileStatus: 'disabled',
				lastError: '',
				lastDisabledAt: new Date().toISOString(),
			});
			// setMessage('Teams trigger disabled.');
		} catch (err: any) {
			const errorMessage = err?.response?.data?.message || err?.message || 'Failed to disable Teams trigger';
			updateNodeValue(id, setNodes, { status: 'error', lastError: errorMessage });
			setMessage(errorMessage);
		} finally {
			setIsActionLoading(false);
		}
	};

	const messageConnected = !!(data.value?.subscriptionId && data.value?.expirationDateTime && new Date(data.value.expirationDateTime).getTime() > Date.now() && data.value?.status === 'connected');
	const fileRequired = scopeType === 'channel' && (eventMode === 'file' || eventMode === 'message_or_file');
	const fileConnected = !fileRequired || !!(data.value?.fileSubscriptionId && data.value?.fileExpirationDateTime && new Date(data.value.fileExpirationDateTime).getTime() > Date.now() && data.value?.fileStatus === 'active');
	const isConnected = messageConnected && fileConnected;
	const isWarning = !connectionId || !isConnected || data.value?.status === 'error';
	const currentScopeItems = scopeOptions.map(option => ({ ...option, isActive: option.id === scopeType }));
	const currentEventModeItems = eventModeOptions.map(option => ({ ...option, isActive: option.id === eventMode }));
	const enableDisabled = isActionLoading || !connectionId || (scopeType === 'channel' ? !(data.value?.teamId || getActiveId(data.value?.teams)) || !(data.value?.channelId || getActiveId(data.value?.channels)) : !(data.value?.chatId || getActiveId(data.value?.chats)));
	const disableDisabled = isActionLoading || (!data.value?.subscriptionId && !data.value?.fileSubscriptionId);

	return (<>
		<SettingsBlock nodeId={id} data={settings} setData={setSettings} />
		<Main>
			{isWarning ? <>
				<div id="warning"></div>
				<span id="warningIcon"><IoWarningOutline size={21} /></span>
			</> : data.output ? <>
				<div id="ran"></div>
				<span id="ranIcon"><IoCheckmarkCircleOutline size={21} /></span>
			</> : null}
			{isExpanded ? <ExpandedBlock
				data={{ id, ...data }}
				handleClose={() => setIsExpanded(false)}
				isWarning={isWarning}
				style={{ minWidth: '450px', maxWidth: '500px' }}
			>
				{isLoading ? <Loader /> : <Styled>
					<AuthenticateSelect
						type="microsoft"
						defaultId={connectionId}
						defaultConnection={data.value?.connection}
						//   handleAuthenticate={auth}
						background={data.background}

					// type="microsoft"
					// defaultId={connectionId}
					// defaultConnection={data.value?.connection}
					handleAuthenticate={handleAuthenticate}
					// background={data.background}
					/>
					<div className="action">
						<button className="btn1 withBorder" onClick={fetchTeams} disabled={!connectionId || isActionLoading}>Load teams</button>
						<button className="btn1 withBorder" onClick={fetchChannels} disabled={!connectionId || isActionLoading || !getActiveId(data.value?.teams)}>Load channels</button>
						<button className="btn1 withBorder" onClick={fetchChats} disabled={!connectionId || isActionLoading}>Load chats</button>
					</div>
					<Select title="Scope" data={currentScopeItems} handleSelectItem={handleSelectScope} isSmall />
					<Select title="Event" data={currentEventModeItems} handleSelectItem={handleSelectEventMode} isSmall />
					{scopeType === 'channel' ? <>
						<Select title="Team" data={data.value?.teams || []} handleSelectItem={(itemId) => handleSelectCollection('teams', itemId)} isSmall emptyMsg="Load teams" />
						<Select title="Channel" data={data.value?.channels || []} handleSelectItem={(itemId) => handleSelectCollection('channels', itemId)} isSmall emptyMsg="Load channels" />
					</> : <Select title="Chat" data={data.value?.chats || []} handleSelectItem={(itemId) => handleSelectCollection('chats', itemId)} isSmall emptyMsg="Load chats" />}

					<div className="action">
						<button className="btn1 withBorder" onClick={enableSubscription} disabled={enableDisabled}>Enable subscription</button>
						<button className="btn1 withBorder danger" onClick={disableSubscription} disabled={disableDisabled}>Disable subscription</button>
					</div>
					<div className={`status ${isConnected ? 'connected' : data.value?.status === 'error' ? 'error' : ''}`}>
						Status: {isConnected ? 'Connected' : data.value?.status || 'Disabled'}
						{/* {data.value?.expirationDateTime ? ` · expires ${new Date(data.value.expirationDateTime).toLocaleString()}` : ''} */}
					</div>
					{message || data.value?.lastError ? <div className="message">{message || data.value?.lastError}</div> : null}

					{/* <div className="webhook-section">
						<div className="webhook-header">
							<h4>Teams Notification URL</h4>
							<button
								type="button"
								className="btn1 withBorder toggle-btn"
								onClick={() => setShowNotificationUrl(prev => !prev)}
							>
								{showNotificationUrl ? 'Hide URL' : 'Show URL'}
							</button>
						</div>
						{showNotificationUrl ? <CopyToClipboard
							text={link}
							onCopy={() => {
								setIsCopy(true);
								setTimeout(() => setIsCopy(false), 500);
							}}
						>
							<div className="link-container" title={link}>
								<div className="link-with-copy">
									<FontAwesomeIcon icon={isCopy ? faCheck : faClipboard} className={`copy-icon ${isCopy ? 'copied' : ''}`} />
									<span className="link-text">{link}</span>
								</div>
							</div>
						</CopyToClipboard> : null}
					</div>

					<div className="instructions-section">
						<h5>Setup Instructions:</h5>
						<ol className="steps-list">
							<li>
								<a
									href="https://learn.microsoft.com/en-us/graph/teams-changenotifications-chatmessage"
									target="_blank"
									rel="noopener noreferrer"
									className="external-link"
								>
									Open Microsoft Graph Teams notifications docs
									<FontAwesomeIcon icon={faExternalLinkAlt} size="xs" />
								</a>
							</li>
							<li>Select a Microsoft connection and channel or chat.</li>
							<li>Click Enable subscription.</li>
							<li>Use Disable subscription before removing this trigger if you want to stop it immediately.</li>
							<li>Subscriptions are created with a short expiry to avoid lifecycle setup.</li>
						</ol>
					</div> */}
				</Styled>}
			</ExpandedBlock> : null}
		</Main>
	</>);
}
